import { LEGAL_SUFFIX_TOKENS } from "./config";

/**
 * Normalize a filename or customer long name into a token list.
 *
 * Used identically for filenames and customer long names. A trailing `.pdf`
 * extension is dropped up front so the same function works for both inputs.
 *
 * Steps:
 *   1. Drop a single trailing `.pdf` (case-insensitive).
 *   2. Unicode NFKD normalize and strip combining diacritics.
 *   3. Lowercase.
 *   4. Replace `&` with ` and `.
 *   5. Collapse any run of non-`[a-z0-9]` characters to a single space.
 *   6. Trim and split on whitespace, dropping empty tokens.
 */
export function normalizeForMatch(value: string): string[] {
  return value
    .replace(/\.pdf$/i, "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter((token: string) => token.length > 0);
}

/**
 * Strip trailing legal-suffix tokens (e.g. "llc", "inc") from a token list,
 * but only if at least one non-suffix token remains.
 *
 * Only trailing tokens are stripped; legal suffix tokens appearing
 * mid-sequence are left untouched.
 */
export function stripLegalSuffixes(tokens: string[]): string[] {
  let end = tokens.length;
  while (end > 0 && LEGAL_SUFFIX_TOKENS.has(tokens[end - 1])) {
    end -= 1;
  }
  // Only apply the strip if at least one meaningful token remains.
  if (end === 0) {
    return tokens.slice();
  }
  return tokens.slice(0, end);
}
